/**
 * Das i-Symbol neben einer Überschrift oder Beschriftung: die Erklärung steht nicht mehr dauerhaft
 * im Bild, sondern erscheint, wenn man darauf zeigt (Maus) oder tippt (Finger, Tastatur).
 *
 * Bastian am 11.10.2026: „maximale Klarheit und simpel" — Sätze, die bei jeder Zeile gleich
 * wiederkehren, lenken vom Entscheiden ab. Verloren geht dabei nichts:
 *  - Der Text bleibt im Dokument (`hidden`, solange zu) und ist über `aria-describedby` die
 *    Beschreibung des Knopfs — eine Vorlesesoftware sagt ihn beim Fokus an, auch ohne Öffnen.
 *  - Ein Klick heftet die Blase an; Escape oder ein Klick daneben schließt sie wieder.
 *  - Hover öffnet nur vorübergehend und schließt beim Verlassen, solange nicht angeheftet.
 *
 * Der Inhalt ist Fließtext (span), damit das Bauteil auch in einem <p> oder <label> stehen darf.
 *
 * Die Blase sitzt am Fenster (position: fixed), nicht am Knopf: in einer Tabellenzelle schnitte der seitlich
 * scrollende Tabellenrahmen sie sonst ab. Reicht der Platz unten nicht, öffnet sie nach oben; rechts bleibt
 * sie im Fenster, und der kleine Pfeil zeigt trotzdem auf das i.
 */

import { useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties, type ReactElement, type ReactNode } from 'react'
import './bausteine.css'

interface Lage { top: number; left: number; pfeil: number; oben: boolean }

const RAND = 8

export interface InfoProps {
  /** Worum es geht — wird Teil des Knopfnamens: „Erklärung: Kein passender Abschluss". */
  titel: string
  /** Kennung der Blase — gesetzt, wenn ein anderes Element (das Eingabefeld eines <Feld>) auf sie
   *  verweisen soll. */
  id?: string
  children: ReactNode
}

export function Info({ titel, id, children }: InfoProps): ReactElement {
  const eigeneId = useId()
  const inhaltId = id ?? eigeneId
  const [angeheftet, setAngeheftet] = useState(false)
  const [schwebt, setSchwebt] = useState(false)
  const huelle = useRef<HTMLSpanElement>(null)
  const knopf = useRef<HTMLButtonElement>(null)
  const blase = useRef<HTMLSpanElement>(null)
  const [lage, setLage] = useState<Lage | null>(null)
  const sichtbar = angeheftet || schwebt

  useLayoutEffect(() => {
    if (!sichtbar) return undefined
    const messen = (): void => {
      const k = knopf.current?.getBoundingClientRect()
      const b = blase.current?.getBoundingClientRect()
      if (k === undefined || b === undefined || (k.width === 0 && k.height === 0)) return
      const left = Math.max(RAND, Math.min(k.left - RAND, window.innerWidth - b.width - RAND))
      const untenPasst = k.bottom + RAND + b.height <= window.innerHeight - RAND
      const oben = !untenPasst && k.top - RAND - b.height >= RAND
      setLage({
        top: oben ? k.top - RAND - b.height : k.bottom + RAND,
        left,
        pfeil: Math.max(10, Math.min(b.width - 18, k.left + k.width / 2 - left - 4.5)),
        oben,
      })
    }
    messen()
    window.addEventListener('scroll', messen, true)
    window.addEventListener('resize', messen)
    return () => {
      window.removeEventListener('scroll', messen, true)
      window.removeEventListener('resize', messen)
    }
  }, [sichtbar])

  const stil: CSSProperties | undefined = lage === null || !sichtbar ? undefined : {
    position: 'fixed', top: lage.top, left: lage.left, ['--pfeil' as string]: `${lage.pfeil}px`,
  }

  useEffect(() => {
    if (!angeheftet) return undefined
    const beiTaste = (ereignis: KeyboardEvent): void => {
      if (ereignis.key === 'Escape') {
        setAngeheftet(false)
        setSchwebt(false)
      }
    }
    const beiZeiger = (ereignis: PointerEvent): void => {
      if (huelle.current !== null && ereignis.target instanceof Node && !huelle.current.contains(ereignis.target)) {
        setAngeheftet(false)
        setSchwebt(false)
      }
    }
    document.addEventListener('keydown', beiTaste)
    document.addEventListener('pointerdown', beiZeiger)
    return () => {
      document.removeEventListener('keydown', beiTaste)
      document.removeEventListener('pointerdown', beiZeiger)
    }
  }, [angeheftet])

  return (
    <span
      ref={huelle}
      className="info"
      // Nur eine Maus „schwebt": ein Finger löst beim Antippen ebenfalls enter aus, aber nie leave —
      // die Blase bliebe nach dem zweiten Tippen offen stehen.
      onPointerEnter={(ereignis) => { if (ereignis.pointerType === 'mouse') setSchwebt(true) }}
      onPointerLeave={(ereignis) => { if (ereignis.pointerType === 'mouse') setSchwebt(false) }}
    >
      <button
        ref={knopf}
        type="button"
        className="info-knopf"
        aria-label={`Erklärung: ${titel}`}
        aria-expanded={sichtbar}
        aria-controls={inhaltId}
        aria-describedby={inhaltId}
        onClick={() => { setAngeheftet((alt) => !alt) }}
      >
        <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true" focusable="false">
          <circle cx="10" cy="10" r="8.25" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <path d="M10 9v4.75" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
          <circle cx="10" cy="6.4" r="1.05" fill="currentColor" />
        </svg>
      </button>
      <span
        ref={blase}
        id={inhaltId}
        className="info-blase"
        data-oben={lage?.oben === true ? 'true' : undefined}
        style={stil}
        role="note"
        hidden={!sichtbar}
      >
        {children}
      </span>
    </span>
  )
}
